import { Button, Dialog, Div, Drawer, Hr, P, Span, Tooltip } from '@dnb/eufemia';
import { SubHeading } from '@dnb/eufemia/extensions/forms/Form';
import { edit, trash as TrashIcon } from '@dnb/eufemia/icons';
import { AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import type { ClientDto } from '@portals/shared/portal/ClientDto';
import classnames from 'classnames';
import { JSX, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Card from '@/components/Card';

import { getTooltipMessage } from '../Credential/components/ClientCard';
import ClientDeleteDrawer from './components/ClientDeleteDrawer';

import style from './index.module.css';

export type ClientProps = {
  isAdmin: boolean;
  hasClientManagementGrp: boolean;
  client: ClientDto;
  onDeleteFromPortalClicked?: (() => void) | null;
  appScopeEntitlementScopeNames: Set<string>;
  noPrivilegeError: ReactNode;
  onDeleteClicked: () => void;
  app: AppWithEntitlementsDto;
};

export default function Client({
  isAdmin,
  client,
  onDeleteFromPortalClicked,
  appScopeEntitlementScopeNames,
  hasClientManagementGrp,
  noPrivilegeError,

  onDeleteClicked,
  app,
}: ClientProps): JSX.Element | null {
  const navigate = useNavigate();

  const extraScopeCount = useMemo(() => {
    return client.scopes.filter((clientScope) => !appScopeEntitlementScopeNames.has(clientScope)).length;
  }, [appScopeEntitlementScopeNames, client]);

  const hasPrivilegeToEditClient = useMemo(() => {
    return isAdmin && hasClientManagementGrp;
  }, [isAdmin, hasClientManagementGrp]);

  const hasScopeEntitlements = app.currentApiAndScopeEntitlements.some(
    ({ scopeEntitlements }) => scopeEntitlements.length > 0,
  );

  return (
    <Card className={style['ClientTile']} key={client.id}>
      <div className={style['TitleAndScopeContainer']}>
        <P>{client.name ?? <i style={{ color: 'gray' }}>No client name</i>}</P>
        {extraScopeCount === 0 ? (
          <P className={style['ScopeText']} size="x-small">
            {client.scopes.length}/{appScopeEntitlementScopeNames.size} scopes
          </P>
        ) : (
          <div className={style['ExtraScopeContainer']}>
            <P className={style['ScopeText']} size="x-small">
              {client.scopes.length - extraScopeCount}/{appScopeEntitlementScopeNames.size} scopes
            </P>
            <div>
              <P className={classnames(style['ScopeWarnText'], `ExtraScopeTooltip-${client.id}`)} size="x-small">
                +{extraScopeCount} extra scopes
              </P>
              <Tooltip className={style['WarnTooltip']} targetSelector={`.ExtraScopeTooltip-${client.id}`}>
                <P>Client has access to extra scopes. Click view/edit for more information.</P>
              </Tooltip>
            </div>
            <div />
          </div>
        )}
      </div>
      <Span className={style['Subtext']}>{client.id}</Span>
      <Hr />
      <Div className={style['ClientTileContent']}>
        <Button
          icon={hasPrivilegeToEditClient ? edit : undefined}
          icon_position="left"
          on_click={() =>
            navigate(
              app.owner.type === 'team'
                ? `/team/${app.owner.id}/application/${app.id}/update-client/${client.id}`
                : `/application/${app.id}/update-client/${client.id}`,
            )
          }
          text={hasPrivilegeToEditClient ? 'Edit' : 'View'}
          variant="tertiary"
        />
        {isAdmin && !hasPrivilegeToEditClient ? (
          <Button
            disabled
            icon={edit}
            icon_position="left"
            text="Edit"
            tooltip={
              hasPrivilegeToEditClient
                ? undefined
                : getTooltipMessage(isAdmin, hasPrivilegeToEditClient, hasScopeEntitlements, 'update')
            }
            variant="tertiary"
          />
        ) : (
          <div>
            <Drawer
              disabled={!hasPrivilegeToEditClient}
              maxWidth="35rem"
              triggerAttributes={{
                tooltip: hasPrivilegeToEditClient
                  ? undefined
                  : getTooltipMessage(isAdmin, hasPrivilegeToEditClient, hasScopeEntitlements, 'delete'),
                text: '',
                icon: TrashIcon,
                variant: 'tertiary',
                icon_position: 'left',
              }}
            >
              <Drawer.Header>
                <SubHeading>{client.name ?? <i style={{ color: 'gray' }}>No client name</i>}</SubHeading>
                <P className={style['Subtext']}>{client.id}</P>
                <P bottom="small" top="small">
                  {client.description}
                </P>
              </Drawer.Header>
              <Drawer.Body>
                <ClientDeleteDrawer
                  client={client}
                  hasPrivilegeToEditClient={hasPrivilegeToEditClient}
                  isAdmin={isAdmin}
                  noPrivilegeError={noPrivilegeError}
                  onDeleteClicked={() => onDeleteClicked()}
                />
              </Drawer.Body>
            </Drawer>
            {isAdmin && onDeleteFromPortalClicked && (
              <Dialog
                confirmText="Delete from portal"
                confirmType="warning"
                declineText="Cancel"
                description="This action will not impact client in ciam."
                icon={TrashIcon}
                onConfirm={({ close }) => {
                  close();
                  onDeleteFromPortalClicked();
                }}
                title="Delete from portal"
                triggerAttributes={{
                  size: 'small',
                  icon: TrashIcon,
                  variant: 'signal',
                }}
                variant="confirmation"
              />
            )}
          </div>
        )}
      </Div>
    </Card>
  );
}
